import {
  signalStoreFeature,
  withComputed,
  withMethods,
  patchState,
  type,
} from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { initialWidgetState, WidgetState } from './widget.state';
import {
  addEntity,
  EntityId,
  removeEntity,
  updateEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  CustomGridStackWidget,
  WidgetType,
} from '../../services/grid-widget-config.service';
import { ChartType } from 'chart.js';
import { EntityChanges } from '@ngrx/signals/entities/src/models';
import { DataGroupProperty } from '../indicatpr-data/indicator-data.widget-selectors';
import { WidgetHelperService } from '../../services/widget/widget-helper.service';

export function withWidgetCoreFeatures() {
  return signalStoreFeature(
    {
      state: type<WidgetState>(),
    },
    withEntities<CustomGridStackWidget>(),
    withComputed(({ entities, entityMap, selectedId }) => ({
      asArray: computed(() => entities()),
      selected: computed(() => {
        const id = selectedId();
        return id ? entityMap()[id] : null;
      }),
      activeWidget: computed(() => {
        const id = selectedId();
        return id ? entityMap()[id].widget : null;
      }),
      widgetType: computed(() => {
        const id = selectedId();
        return id ? entityMap()[id].type : null;
      }),
      copyFromSelected: computed(() => {
        const id = selectedId();
        return id ? { ...entityMap()[id] } : null;
      }),
    })),
    withMethods((store, helper = inject(WidgetHelperService)) => ({
      reset() {
        patchState(store, initialWidgetState);
      },
      select(id: string): void {
        patchState(store, { selectedId: id, rerenderId: '', event: '' });
      },
      unselect(): void {
        patchState(store, { selectedId: null, rerenderId: '', event: '' });
      },
      add(widget: CustomGridStackWidget): void {
        patchState(store, addEntity(widget), { rerenderId: '', event: '' });
      },
      remove(id: string): void {
        patchState(store, removeEntity(id), { rerenderId: '', event: '' });
      },
      rerenderWidget(id: EntityId): void {
        patchState(store, {
          rerenderId: id,
        });
      },
      update(
        id: EntityId,
        changes: Partial<CustomGridStackWidget>,
        rerender = false,
        event = ''
      ): void {
        patchState(store, updateEntity({ id, changes }), {
          rerenderId: rerender ? id : '',
          event: event,
        });
      },
      triggerEvent(event: string, eventOptions?: any): void {
        setTimeout(() => {
          patchState(store, {
            event,
            eventOptions,
          });
          queueMicrotask(() =>
            patchState(store, {
              event: '',
              eventOptions: undefined,
            })
          );
        }, 400);
      },
      bulkUpdate(widgets: Partial<CustomGridStackWidget>[]): void {
        const ids: EntityId[] = widgets.map(widget => widget._id);
        patchState(store, {
          rerenderIds: ids,
        });
        setTimeout(() => {
          patchState(store, {
            rerenderIds: [],
          });
        }, 100);
      },
      get(id: EntityId) {
        return store.entities().find(entity => entity.id === id) || null;
      },
      getType(id: EntityId) {
        return this.get(id)?.type as WidgetType;
      },
      setWidgetToExport(id: string | null) {
        patchState(store, { widgetToExport: id });
      },
      getByFocusProperty(type: DataGroupProperty) {
        return store
          .entities()
          .filter(
            entity =>
              entity.widget.focusProperty === type &&
              helper.isDataWidget(entity.type)
          );
      },
    }))
  );
}
